import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, XMarkIcon, Cog6ToothIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

const DAYS      = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const HOL_TYPES = [
  { value:'public',    label:'Public Holiday',   color:'#ef4444', bg:'#fef2f2' },
  { value:'school',    label:'School Holiday',    color:'#3b6ef8', bg:'#eef2ff' },
  { value:'emergency', label:'Emergency Closure', color:'#f59e0b', bg:'#fffbeb' },
];

const VAC_META = {
  summer: { label:'Summer', color:'#f59e0b', bg:'#fffbeb', icon:'☀️' },
  winter: { label:'Winter', color:'#3b6ef8', bg:'#eef2ff', icon:'❄️' },
  spring: { label:'Spring', color:'#10c98e', bg:'#ecfdf5', icon:'🌸' },
  other:  { label:'Other',  color:'#9094a8', bg:'#f0f2f7', icon:'📅' },
};
const SAL_META = {
  none:         { label:'No Salary',          color:'#ef4444' },
  proportional: { label:'Proportional Salary', color:'#f59e0b' },
  full:         { label:'Full Salary',         color:'#10c98e' },
};
const LEAVE_TYPES = ['sick','casual','emergency','maternity','annual','unpaid','other'];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .hol * { box-sizing:border-box; }
  .hol { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .hol-title { font-size:1.4rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:3px; }
  .hol-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }
  .req-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:9px 14px; font-size:.76rem; color:#9a3412; font-weight:600; margin-bottom:1.2rem; }
  /* Page tabs */
  .page-tabs { display:flex; gap:6px; margin-bottom:1.5rem; }
  .page-tab  { display:inline-flex; align-items:center; gap:7px; padding:8px 18px; border-radius:10px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .page-tab svg { width:15px; height:15px; stroke-width:2; }
  .page-tab.active { background:#3b6ef8; color:white; border-color:#3b6ef8; }
  /* Calendar */
  .cal-header { display:flex; align-items:center; gap:.7rem; margin-bottom:1.2rem; flex-wrap:wrap; }
  .cal-nav { width:32px; height:32px; border-radius:8px; border:1.5px solid #e0e4f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:.9rem; color:#6b7194; transition:all .15s; }
  .cal-nav:hover { border-color:#3b6ef8; color:#3b6ef8; }
  .cal-label { font-size:1rem; font-weight:700; color:#0f1123; min-width:140px; text-align:center; }
  .btn-req-add { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; margin-left:auto; }
  .btn-req-add svg { width:14px; height:14px; stroke-width:2.5; }
  /* Calendar grid */
  .cal-grid { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .cal-days-header { display:grid; grid-template-columns:repeat(7,1fr); background:#fafbff; border-bottom:1px solid #edf0f9; }
  .cal-day-hdr { padding:8px; text-align:center; font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .cal-day-hdr.off { color:#ef4444; }
  .cal-cells { display:grid; grid-template-columns:repeat(7,1fr); }
  .cal-cell { min-height:70px; padding:6px; border-right:1px solid #f3f5fb; border-bottom:1px solid #f3f5fb; position:relative; }
  .cal-cell:nth-child(7n) { border-right:none; }
  .cal-cell.off-day { background:#fef9f9; }
  .cal-cell.today   { background:#f0f6ff; }
  .cal-cell.other-month { opacity:.3; }
  .cal-date { font-size:.72rem; font-weight:600; color:#6b7194; margin-bottom:3px; }
  .cal-cell.today .cal-date { color:#3b6ef8; font-weight:700; }
  .hol-chip { font-size:.62rem; font-weight:700; padding:2px 6px; border-radius:5px; margin-top:2px; cursor:pointer; display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  /* Holiday list */
  .hol-list { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-top:1.2rem; }
  .hol-list-header { padding:12px 16px; border-bottom:1px solid #edf0f9; font-size:.8rem; font-weight:700; color:#0f1123; display:flex; align-items:center; justify-content:space-between; }
  .hol-row { display:flex; align-items:center; gap:12px; padding:11px 16px; border-bottom:1px solid #f3f5fb; }
  .hol-row:last-child { border-bottom:none; }
  .hol-date-box { width:46px; height:46px; border-radius:10px; display:flex; flex-direction:column; align-items:center; justify-content:center; flex-shrink:0; }
  .hol-date-day  { font-size:1.1rem; font-weight:700; line-height:1; }
  .hol-date-mon  { font-size:.58rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; margin-top:1px; }
  .hol-name  { font-size:.84rem; font-weight:600; color:#0f1123; }
  .hol-meta  { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .type-badge { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; }
  .paid-badge   { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .unpaid-badge { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#fef2f2; color:#ef4444; }
  .hol-actions { display:flex; gap:4px; margin-left:auto; }
  .hol-btn { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .hol-btn svg { width:13px; height:13px; stroke-width:2; }
  .hol-btn.edit:hover { background:#eef2ff; border-color:#c7d2fe; color:#3b6ef8; }
  .hol-empty { text-align:center; padding:2rem; color:#9094a8; font-size:.8rem; }
  /* Settings read-only */
  .sett-section { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1.2rem; }
  .sett-title { font-size:.92rem; font-weight:700; color:#0f1123; margin-bottom:.3rem; }
  .sett-sub   { font-size:.74rem; color:#9094a8; margin-bottom:1.2rem; }
  .day-grid { display:flex; gap:8px; flex-wrap:wrap; }
  .day-pill { padding:7px 14px; border-radius:9px; border:1.5px solid; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; }
  .day-pill.off  { background:#fef2f2; color:#ef4444; border-color:#fecaca; }
  .day-pill.on   { background:white; color:#6b7194; border-color:#e0e4f0; }
  /* Sat policy cards */
  .sat-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .sat-card { padding:12px 14px; border-radius:10px; border:1.5px solid; }
  .sat-card.active { border-color:#3b6ef8; background:#eef2ff; }
  .sat-card.inactive { border-color:#e0e4f0; background:white; }
  /* Leave policy table */
  .lp-table { width:100%; border-collapse:collapse; }
  .lp-table th { padding:8px 12px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; border-bottom:1px solid #edf0f9; }
  .lp-table td { padding:9px 12px; font-size:.82rem; border-bottom:1px solid #f3f5fb; text-align:center !important; }
  .lp-table tr:last-child td { border-bottom:none; }
  .impact-pill { font-size:.72rem; font-weight:700; padding:2px 8px; border-radius:99px; }
  .impact-paid   { background:#ecfdf5; color:#10c98e; }
  .impact-half   { background:#f5f3ff; color:#8b5cf6; }
  .impact-unpaid { background:#fef2f2; color:#ef4444; }
  .lp-cards { display:none; padding:0; }
  .lp-card  { background:white; border:1px solid #e8ebf4; border-radius:12px; padding:13px; margin-bottom:8px; }
  .lc-row  { display:flex; justify-content:space-between; align-items:center; padding:4px 0; gap:10px; }
  .lc-row:not(:last-child) { border-bottom:1px solid #f3f5fb; }
  .lc-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; flex-shrink:0; }
  .lc-desc { font-size:.74rem; color:#9094a8; text-align:right; }
  /* Stats row */
  .stat-row { display:flex; gap:1rem; flex-wrap:wrap; margin-bottom:1rem; }
  .stat-pill-box { background:#f8f9fc; border:1px solid #e8ebf4; border-radius:9px; padding:7px 14px; font-size:.76rem; color:#6b7194; font-weight:600; }
  .stat-pill-box span { color:#0f1123; font-family:'JetBrains Mono',monospace; }
  .sa-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .sa-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  /* Modal */
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:460px; max-height:92vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; overflow-y:auto; display:flex; flex-direction:column; gap:12px; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-shrink:0; }
  .ff { display:flex; flex-direction:column; gap:5px; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; }
  .fi:focus,.fs:focus { border-color:#3b6ef8; background:white; }
  .toggle-row { display:flex; align-items:center; gap:10px; }
  .toggle-lbl { font-size:.8rem; color:#6b7194; font-weight:600; }
  .btn-save  { padding:9px 20px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-save:disabled { opacity:.6; cursor:not-allowed; }
  .btn-ghost { padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  @media (max-width:768px) {
    .lp-table-wrap { display:none !important; }
    .lp-cards { display:block; }
  }
`;

export default function CoordHolidays() {
  const [tab,       setTab]       = useState('calendar');
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [holidays,  setHolidays]  = useState([]);
  const [vacations, setVacations] = useState([]);
  const [settings,  setSettings]  = useState({ weeklyOffDays:[0], saturdayPolicy:'all_off', leavePolicy:{}, absentDeductionPerDay:0, latePenaltyPerMinute:0, earlyExitPenaltyPerMinute:0, annualPaidLeaves:10, annualCasualLeaves:10, annualSickLeaves:10 });
  const [loading,   setLoading]   = useState(false);
  // Modal state: mode = 'add' | 'edit' | 'mark_offs'
  const [modal,     setModal]     = useState(null); // { mode, holiday? }
  const [form,      setForm]      = useState({ name:'', date:'', type:'school', isPaid:true, notes:'' });
  const [saving,    setSaving]    = useState(false);

  useEffect(() => { loadAll(); }, [year]);
  useEffect(() => { loadSettings(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [hRes, vRes] = await Promise.all([
        api.get(`/coordinator/holidays?year=${year}`),
        api.get(`/coordinator/vacations?year=${year}`).catch(() => ({ data: [] })),
      ]);
      setHolidays(Array.isArray(hRes.data) ? hRes.data : []);
      setVacations(Array.isArray(vRes.data) ? vRes.data : []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get('/coordinator/holidays/settings');
      setSettings(prev => ({ ...prev, ...data }));
    } catch {}
  };

  // Calendar helpers
  const today    = new Date();
  const firstDay = new Date(year, viewMonth, 1);
  const lastDay  = new Date(year, viewMonth + 1, 0);
  const startPad = firstDay.getDay();

  const cells = [];
  for (let i = 0; i < startPad; i++)
    cells.push({ date: new Date(year, viewMonth, -startPad + i + 1), isCurrentMonth: false });
  for (let d = 1; d <= lastDay.getDate(); d++)
    cells.push({ date: new Date(year, viewMonth, d), isCurrentMonth: true });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ date: new Date(year, viewMonth + 1, d), isCurrentMonth: false });

  const getHolidaysForDate = (date) => holidays.filter(h => {
    const hd = new Date(h.date);
    return hd.getFullYear() === date.getFullYear() && hd.getMonth() === date.getMonth() && hd.getDate() === date.getDate();
  });

  const isOffDay = (date) => settings.weeklyOffDays?.includes(date.getDay());

  const openAdd = () => {
    setForm({ name:'', date:`${year}-${String(viewMonth+1).padStart(2,'0')}-01`, type:'school', isPaid:true, notes:'' });
    setModal({ mode:'add' });
  };

  const openEdit = (h) => {
    setForm({ name:h.name, date:new Date(h.date).toISOString().slice(0,10), type:h.type, isPaid:h.isPaid, notes:h.notes||'' });
    setModal({ mode:'edit', holiday:h });
  };

  const submitRequest = async () => {
    if (modal.mode === 'mark_offs') {
      const month = `${year}-${String(viewMonth+1).padStart(2,'0')}`;
      setSaving(true);
      try {
        const { data } = await api.post('/coordinator/mark-weekly-offs', { month });
        toast.success(data.message || `Weekly off-days marked for ${MONTHS[viewMonth]} ${year}`);
        setModal(null);
      } catch { toast.error('Failed to mark off-days'); }
      finally { setSaving(false); }
      return;
    }
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      const ep = {
        add:  '/coordinator/request/holiday-add',
        edit: '/coordinator/request/holiday-edit',
      }[modal.mode];
      const payload = { ...form, ...(modal.mode === 'edit' ? { holidayId: modal.holiday._id } : {}) };
      await api.post(ep, payload);
      toast.success('Request submitted for admin approval');
      setModal(null);
    } catch { toast.error('Failed to submit'); }
    finally { setSaving(false); }
  };


  const monthHolidays = holidays.filter(h => new Date(h.date).getMonth() === viewMonth);

  return (
    <div className="hol"><style>{css}</style>
      <div className="hol-title">Holidays & Leave Policy</div>
      <div className="hol-sub">View holidays, vacations and leave policy settings.</div>
      <div className="req-banner">⚠️ Holiday add/edit actions are submitted as requests — admin must approve before they take effect.</div>

      {/* Page tabs — identical to admin */}
      <div className="page-tabs">
        <button className={`page-tab ${tab==='calendar'?'active':''}`} onClick={() => setTab('calendar')}>
          <CalendarDaysIcon />Calendar
        </button>
        <button className={`page-tab ${tab==='vacations'?'active':''}`} onClick={() => setTab('vacations')}>
          <svg style={{width:15,height:15,strokeWidth:2,stroke:'currentColor',fill:'none'}} viewBox="0 0 24 24"><path d="M3 9h18M9 3v18M15 3v18" strokeLinecap="round"/></svg>
          Vacations
        </button>
        <button className={`page-tab ${tab==='settings'?'active':''}`} onClick={() => setTab('settings')}>
          <Cog6ToothIcon />Settings
        </button>
      </div>

      {/* ── Calendar Tab ── */}
      {tab === 'calendar' && (
        <>
          <div className="cal-header">
            <button className="cal-nav" onClick={() => { if (viewMonth===0){setViewMonth(11);setYear(y=>y-1);}else setViewMonth(m=>m-1); }}>←</button>
            <div className="cal-label">{MONTHS[viewMonth]} {year}</div>
            <button className="cal-nav" onClick={() => { if (viewMonth===11){setViewMonth(0);setYear(y=>y+1);}else setViewMonth(m=>m+1); }}>→</button>
            <button className="cal-nav" onClick={() => { setYear(today.getFullYear()); setViewMonth(today.getMonth()); }}>Today</button>
            <button
              onClick={() => setModal({ mode:'mark_offs' })}
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 14px',border:'1.5px solid #e0e4f0',borderRadius:10,background:'white',fontFamily:'Sora',fontSize:'.8rem',fontWeight:600,cursor:'pointer',color:'#6b7194',transition:'all .15s'}}
              onMouseOver={e=>{e.currentTarget.style.borderColor='#3b6ef8';e.currentTarget.style.color='#3b6ef8';}}
              onMouseOut={e=>{e.currentTarget.style.borderColor='#e0e4f0';e.currentTarget.style.color='#6b7194';}}>
              📅 Mark Off-Days
            </button>
            <button className="btn-req-add" onClick={openAdd}><PlusIcon />Request Add Holiday</button>
          </div>

          {loading ? (
            <div className="sa-loading"><div className="sa-spin"/>Loading…</div>
          ) : (
            <>
              <div className="cal-grid">
                <div className="cal-days-header">
                  {DAYS.map((d,i) => (
                    <div key={d} className={`cal-day-hdr ${settings.weeklyOffDays?.includes(i)?'off':''}`}>{d}</div>
                  ))}
                </div>
                <div className="cal-cells">
                  {cells.map((cell, idx) => {
                    const cellHols = getHolidaysForDate(cell.date);
                    const isOff    = isOffDay(cell.date);
                    const isToday  = cell.date.toDateString() === today.toDateString();
                    return (
                      <div key={idx} className={`cal-cell${!cell.isCurrentMonth?' other-month':''}${isOff?' off-day':''}${isToday?' today':''}`}>
                        <div className="cal-date">{cell.date.getDate()}</div>
                        {isOff && cell.isCurrentMonth && <div style={{fontSize:'.58rem',color:'#ef4444',fontWeight:600}}>Off</div>}
                        {cellHols.map(h => {
                          const t = HOL_TYPES.find(x => x.value === h.type) || HOL_TYPES[0];
                          return (
                            <div key={h._id} className="hol-chip"
                              style={{background:t.bg,color:t.color}}
                              onClick={() => openEdit(h)}
                              title={h.name}>
                              {h.name}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Holiday list for current month */}
              <div className="hol-list">
                <div className="hol-list-header">
                  <span>{MONTHS[viewMonth]} {year} — {monthHolidays.length} holiday{monthHolidays.length!==1?'s':''}</span>
                </div>
                {monthHolidays.length === 0 ? (
                  <div className="hol-empty">No holidays this month.</div>
                ) : monthHolidays.map(h => {
                  const d  = new Date(h.date);
                  const t  = HOL_TYPES.find(x => x.value === h.type) || HOL_TYPES[0];
                  return (
                    <div key={h._id} className="hol-row">
                      <div className="hol-date-box" style={{background:t.bg,color:t.color}}>
                        <div className="hol-date-day">{d.getDate()}</div>
                        <div className="hol-date-mon">{MONTHS[d.getMonth()]}</div>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div className="hol-name">{h.name}</div>
                        <div className="hol-meta">{DAYS[d.getDay()]} · {h.notes||'No notes'}</div>
                      </div>
                      <span className="type-badge" style={{background:t.bg,color:t.color}}>{t.label}</span>
                      <span className={h.isPaid?'paid-badge':'unpaid-badge'}>{h.isPaid?'Paid':'Unpaid'}</span>
                      <div className="hol-actions">
                        {/* Edit → opens request modal */}
                        <button className="hol-btn edit" onClick={() => openEdit(h)} title="Request Edit"><PencilIcon /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Vacations Tab — read-only ── */}
      {tab === 'vacations' && (
        <>
          <div style={{marginBottom:'1.2rem'}}>
            <div style={{fontSize:'.92rem',fontWeight:700,color:'#0f1123'}}>Vacation Periods</div>
            <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:2}}>Summer/winter/spring breaks set by admin. Salary = days worked × (monthlySalary ÷ 30).</div>
          </div>
          {loading ? (
            <div className="sa-loading"><div className="sa-spin"/>Loading…</div>
          ) : vacations.length === 0 ? (
            <div style={{background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'3rem',textAlign:'center',color:'#9094a8',fontSize:'.82rem'}}>No vacation periods added yet.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {vacations.map(v => {
                const vt  = VAC_META[v.type] || VAC_META.other;
                const sr  = SAL_META[v.salaryRule] || SAL_META.proportional;
                const days = Math.round((new Date(v.endDate) - new Date(v.startDate)) / 86400000) + 1;
                return (
                  <div key={v._id} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.3rem',display:'flex',alignItems:'center',gap:14}}>
                    <div style={{width:46,height:46,borderRadius:12,background:vt.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.3rem',flexShrink:0}}>{vt.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{v.name}</div>
                      <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:2}}>
                        {new Date(v.startDate).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'})} → {new Date(v.endDate).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'})} · <strong>{days} days</strong>
                      </div>
                      {v.notes && <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:2}}>{v.notes}</div>}
                    </div>
                    <span style={{fontSize:'.72rem',fontWeight:700,padding:'3px 10px',borderRadius:99,background:vt.bg,color:vt.color}}>{vt.label}</span>
                    <span style={{fontSize:'.72rem',fontWeight:700,color:sr.color}}>{sr.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Settings Tab — read-only ── */}
      {tab === 'settings' && (
        <>
          {/* Weekly Off-Days */}
          <div className="sett-section">
            <div className="sett-title">Weekly Off-Days</div>
            <div className="sett-sub">Days of the week that are off — set by admin.</div>
            <div className="day-grid">
              {DAYS.filter((_,i) => i !== 6).map((d, i) => (
                <div key={d} className={`day-pill ${settings.weeklyOffDays?.includes(i)?'off':'on'}`}>
                  {d} {settings.weeklyOffDays?.includes(i) ? '✗' : '✓'}
                </div>
              ))}
            </div>
            <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:10}}>
              Currently off: <strong>{settings.weeklyOffDays?.filter(i=>i!==6).map(i=>DAYS[i]).join(', ') || 'None'}</strong> (Saturday handled by policy below)
            </div>
          </div>

          {/* Saturday Policy */}
          <div className="sett-section">
            <div className="sett-title">Saturday Policy</div>
            <div className="sett-sub">How Saturday is handled — set by admin.</div>
            <div className="sat-grid">
              {[
                { value:'all_off',            label:'All Saturdays Off',          desc:'Every Saturday is a holiday' },
                { value:'all_on',             label:'All Saturdays Working',       desc:'Every Saturday is a working day' },
                { value:'alternate_odd_off',  label:'1st & 3rd Saturday Off',     desc:'2nd & 4th Saturday are working days' },
                { value:'alternate_even_off', label:'2nd & 4th Saturday Off',     desc:'1st & 3rd Saturday are working days' },
              ].map(opt => (
                <div key={opt.value} className={`sat-card ${settings.saturdayPolicy===opt.value?'active':'inactive'}`}>
                  <div style={{fontWeight:700,fontSize:'.82rem',color:settings.saturdayPolicy===opt.value?'#3b6ef8':'#0f1123',marginBottom:3}}>{opt.label}</div>
                  <div style={{fontSize:'.72rem',color:'#9094a8'}}>{opt.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Salary deduction */}
          <div className="sett-section">
            <div className="sett-title">Salary Deduction Rules</div>
            <div className="sett-sub">Applied when calculating monthly salary — set by admin.</div>
            <div className="stat-row">
              <div className="stat-pill-box">Absent / Day: <span>Rs. {settings.absentDeductionPerDay||0}</span></div>
              <div className="stat-pill-box">Late / Min: <span>Rs. {settings.latePenaltyPerMinute||0}</span></div>
              <div className="stat-pill-box">Early Exit / Min: <span>Rs. {settings.earlyExitPenaltyPerMinute||0}</span></div>
            </div>
          </div>

          {/* Leave policy */}
          <div className="sett-section">
            <div className="sett-title">Leave Policy</div>
            <div className="sett-sub">Whether each leave type is paid, half-paid, or unpaid — set by admin.</div>
            <div className="lp-table-wrap">
              <table className="lp-table">
                <thead><tr><th>Leave Type</th><th>Salary Impact</th><th>Effect</th></tr></thead>
                <tbody>
                  {LEAVE_TYPES.map(lt => {
                    const impact = settings.leavePolicy?.[lt] || 'unpaid';
                    return (
                      <tr key={lt}>
                        <td style={{fontWeight:600,textTransform:'capitalize'}}>{lt}</td>
                        <td>
                          <span className={`impact-pill impact-${impact}`}>
                            {impact==='paid'?'Paid':impact==='half'?'Half-paid':'Unpaid'}
                          </span>
                        </td>
                        <td style={{fontSize:'.74rem',color:'#9094a8'}}>
                          {impact==='paid'?'No salary cut':impact==='half'?'Half day deducted':'Full day deducted'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="lp-cards">
              {LEAVE_TYPES.map(lt => {
                const impact = settings.leavePolicy?.[lt] || 'unpaid';
                return (
                  <div key={lt} className="lp-card">
                    <div className="lc-row">
                      <span className="lc-label">Leave Type</span>
                      <span style={{fontWeight:600,textTransform:'capitalize'}}>{lt}</span>
                    </div>
                    <div className="lc-row">
                      <span className="lc-label">Salary Impact</span>
                      <span className={`impact-pill impact-${impact}`}>
                        {impact==='paid'?'Paid':impact==='half'?'Half-paid':'Unpaid'}
                      </span>
                    </div>
                    <div className="lc-row">
                      <span className="lc-label">Effect</span>
                      <span className="lc-desc">{impact==='paid'?'No salary cut':impact==='half'?'Half day deducted':'Full day deducted'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Annual quotas */}
          <div className="sett-section">
            <div className="sett-title">Annual Leave Quota</div>
            <div className="sett-sub">Maximum paid leaves allowed per year per staff member — set by admin.</div>
            <div className="stat-row">
              <div className="stat-pill-box">Annual/Paid: <span>{settings.annualPaidLeaves||0} days</span></div>
              <div className="stat-pill-box">Casual: <span>{settings.annualCasualLeaves||0} days</span></div>
              <div className="stat-pill-box">Sick: <span>{settings.annualSickLeaves||0} days</span></div>
              {settings.annualEmergencyLeaves != null && <div className="stat-pill-box">Emergency: <span>{settings.annualEmergencyLeaves||0} days</span></div>}
              {settings.annualMaternityLeaves != null && <div className="stat-pill-box">Maternity: <span>{settings.annualMaternityLeaves||0} days</span></div>}
            </div>
            <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:4}}>These are set in admin settings. Contact admin to change quotas.</div>
          </div>
        </>
      )}

      {/* ── Request Modal (add / edit / mark off-days) ── */}
      {modal && createPortal(
        <div className="mb" onClick={() => setModal(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">
                {modal.mode==='add'?'🎉 Request Add Holiday':modal.mode==='edit'?'✏️ Request Edit Holiday':'📅 Mark Weekly Off-Days'}
              </div>
              <button className="mo-x" onClick={() => setModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              {modal.mode === 'mark_offs' ? (
                <div style={{background:'#eef2ff',border:'1px solid #c7d2fe',borderRadius:9,padding:'12px 14px',fontSize:'.82rem',color:'#3730a3'}}>
                  <div style={{fontWeight:700,marginBottom:6}}>Mark all weekly off-days for <span style={{color:'#3b6ef8'}}>{MONTHS[viewMonth]} {year}</span></div>
                  <div style={{fontSize:'.76rem',color:'#6b7194'}}>This will mark attendance as <strong>holiday</strong> for all configured off-days (currently: <strong>{(settings.weeklyOffDays||[]).map(i=>DAYS[i]).join(', ')||'None'}</strong>) throughout the month. Staff attendance records will be created/updated directly.</div>
                </div>
              ) : (
                <div style={{background:'#f0fdfa',border:'1px solid #a7f3d0',borderRadius:9,padding:'8px 12px',fontSize:'.74rem',color:'#065f46',fontWeight:600}}>
                  This will be submitted as a request — admin must approve before it takes effect.
                </div>
              )}

              {modal.mode !== 'mark_offs' ? <>
                <div className="ff">
                  <label className="fl">Holiday Name *</label>
                  <input className="fi" value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Eid ul Fitr, Independence Day" />
                </div>
                <div className="ff">
                  <label className="fl">Date *</label>
                  <input type="date" className="fi" value={form.date} onChange={e => setForm(p=>({...p,date:e.target.value}))} />
                </div>
                <div className="ff">
                  <label className="fl">Type</label>
                  <select className="fs" value={form.type} onChange={e => setForm(p=>({...p,type:e.target.value}))}>
                    {HOL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="toggle-row">
                  <input type="checkbox" id="isPaid" checked={form.isPaid} onChange={e => setForm(p=>({...p,isPaid:e.target.checked}))} />
                  <label htmlFor="isPaid" className="toggle-lbl">Paid Holiday — staff salary not deducted, attendance auto-marked as holiday</label>
                </div>
                <div className="ff">
                  <label className="fl">Notes (optional)</label>
                  <input className="fi" value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))} placeholder="Any notes…" />
                </div>
              </> : null}
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-save" onClick={submitRequest} disabled={saving}>
                {saving ? (modal.mode==='mark_offs'?'Marking…':'Submitting…') : modal.mode==='mark_offs'?'Mark Off-Days':'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}